/**
 * Module 'readiness': what Orgus found on a machine after pairing (R-ON-4). For each agent
 * harness (Claude Code, Codex, any adapter kind): installed, version and sign-in state; git
 * and whether its identity is configured; and the repositories found under the home folder.
 *
 * The shape is built so it cannot carry a credential: sign-in is one of three states, never
 * a token, key, email or account id; git reports whether an identity is set, and the name
 * only when the owner allows it, never the email; repository paths are relative to the home
 * folder. A node is untrusted, so a server keeps a report only through acceptReadiness().
 *
 * On the wire (ADR 0090 decision 4):
 *
 *   agent -> server  hello's machine.readiness, when the probe finished within
 *                    READINESS_HELLO_WAIT_MS (a slow probe never holds hello back)
 *   agent -> server  { ext: 'readiness', type: 'report', data: MachineReadiness } when a probe
 *                    that missed hello finishes, every READINESS_REFRESH_MS, and after a refresh
 *   server -> agent  { ext: 'readiness', type: 'refresh' }: probe again now (refused by a
 *                    ceiling that denies module messages; the timer still refreshes)
 *   server -> console  Machine.readiness on GET /api/state and the SSE machine upserts
 */
import type { Ts } from '../index.ts';
import { isAgentKindToken } from './adapters.ts';
import type { AgentKind } from './adapters.ts';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Whether a harness CLI is signed in. A state only: never the token, key, email or account behind it. */
export type SignInState = 'signed-in' | 'signed-out' | 'unknown';

export const SIGN_IN_STATES: readonly SignInState[] = ['signed-in', 'signed-out', 'unknown'];

export const SIGN_IN_LABEL: Record<SignInState, string> = {
  'signed-in': 'Signed in',
  'signed-out': 'Signed out',
  unknown: 'Sign-in unknown',
};

/** One agent harness on the machine. */
export interface HarnessReadiness {
  kind: AgentKind;
  installed: boolean;
  /** The version the CLI reports, for example 2.1.3; null when not installed or unknown. */
  version: string | null;
  /** 'unknown' when the harness is not installed or the adapter cannot tell. */
  signIn: SignInState;
}

/**
 * The readiness hook an agent adapter implements (agents/agent-ts AgentAdapter): what its
 * CLI looks like on this machine. Reads local state only and never returns a credential.
 */
export type HarnessReadinessProbe = () => Promise<HarnessReadiness>;

export interface GitReadiness {
  installed: boolean;
  /** `git --version` without the leading "git version", for example 2.47.1; null when unknown. */
  version: string | null;
  /** user.name and user.email are both set in the global git config. */
  identityConfigured: boolean;
  /**
   * The configured user.name, present only when the machine's owner allowed Orgus to show it
   * (a consent setting on the node, off by default). Absent otherwise. The email is never
   * reported, with or without consent.
   */
  identityName?: string;
}

export interface ReposFound {
  /** Every git repository the scan found, including those not listed in paths. */
  count: number;
  /** Paths relative to the home folder with forward slashes, at most READINESS_LIMITS.repoPaths. */
  paths: string[];
  /** count is larger than paths.length. */
  truncated: boolean;
}

/** What a machine reports about itself for the first-run screen (R-ON-4). */
export interface MachineReadiness {
  checkedAt: Ts;
  harnesses: HarnessReadiness[];
  git: GitReadiness;
  repos: ReposFound;
}

/** The module's name on the wire, and its message types. */
export const READINESS_EXT = 'readiness';
export const READINESS_REPORT = 'report';
export const READINESS_REFRESH = 'refresh';
/** How long hello waits for a probe that is still running; past it, the report follows as a 'report'. */
export const READINESS_HELLO_WAIT_MS = 2_000;
/** How often an agent probes again and sends the report. */
export const READINESS_REFRESH_MS = 10 * 60_000;
/** A report larger than this (UTF-8 JSON, after sanitising) is not kept: the last one stands. */
export const READINESS_MAX_BYTES = 16 * 1024;

export const READINESS_LIMITS = Object.freeze({
  harnesses: 16,
  version: 60,
  identityName: 100,
  repoPaths: 50,
  repoPath: 300,
  repoCount: 1_000_000,
  /** Entries of an incoming list looked at, valid or not, so a huge list costs a bounded scan. */
  scan: 500,
});

// ---------------------------------------------------------------------------
// Secret shapes
// ---------------------------------------------------------------------------

/**
 * Credential formats by shape. A protocol copy of the high-precision patterns in
 * @orgus/policy (redact.ts TOKEN_PATTERNS), which depends on this package and so cannot be
 * imported here; kept in step by hand. No /g flag: these are tests, not replacers.
 */
const SECRET_SHAPES: readonly RegExp[] = [
  /-----BEGIN [A-Z0-9 ]*PRIVATE KEY-----/,
  /\bsk-ant-[A-Za-z0-9_-]{20,}/,
  /\bsk-(?:proj-|svcacct-)?[A-Za-z0-9_-]{32,}/,
  /\b(?:gh[pousr]_[A-Za-z0-9]{36,}|github_pat_[A-Za-z0-9_]{22,})/,
  /\b(?:AKIA|ASIA)[0-9A-Z]{16}\b/,
  /\bxox[abposr]-[A-Za-z0-9-]{10,}/,
  /\bAIza[0-9A-Za-z_-]{35}/,
  /\b(?:sk|rk|pk)_(?:live|test)_[A-Za-z0-9]{16,}/,
  /\bnpm_[A-Za-z0-9]{36}\b/,
  /\beyJ[A-Za-z0-9_-]{8,}\.eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}/,
  /\bBearer\s+[A-Za-z0-9._~+/=-]{20,}/i,
  /\b[a-z][a-z0-9+.-]*:\/\/[^\s:/@]+:[^\s@/]{3,}@/i,
  /(?:password|passwd|secret|token|api[_-]?key|access[_-]?key|client[_-]?secret)["']?\s*[=:]/i,
];

/** An email address anywhere in the string. */
const EMAIL_RE = /[^\s@<>]+@[^\s@<>]+\.[A-Za-z]{2,}/;
/** A long run of key-like characters that mixes letters and digits: a generated token. */
const OPAQUE_RE = /[A-Za-z0-9_+/=-]{32,}/;

function opaqueToken(s: string): boolean {
  const m = s.match(new RegExp(OPAQUE_RE.source, 'g'));
  return !!m?.some((run) => /[A-Za-z]/.test(run) && /\d/.test(run));
}

/**
 * True when a string may hold a credential or an email: a known token format, a key-like
 * assignment, a password in a URL, an email address, or a long opaque run of letters and
 * digits. Readiness fields are short and plain, so a false positive only drops a field.
 */
export function looksLikeSecret(s: string): boolean {
  return EMAIL_RE.test(s) || opaqueToken(s) || SECRET_SHAPES.some((re) => re.test(s));
}

// ---------------------------------------------------------------------------
// Sanitisers
// ---------------------------------------------------------------------------

const CONTROL_RE = /[\u0000-\u001f\u007f-\u009f\u2028\u2029]/;

const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

/** A version: plain characters only, trimmed and clipped, never secret-shaped; null otherwise. */
export function sanitizeVersion(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const s = v.trim().slice(0, READINESS_LIMITS.version).trim();
  if (!s || !/^[A-Za-z0-9][A-Za-z0-9 ._+()/-]*$/.test(s) || looksLikeSecret(s)) return null;
  return s;
}

export function sanitizeSignIn(v: unknown): SignInState {
  return typeof v === 'string' && (SIGN_IN_STATES as readonly string[]).includes(v) ? (v as SignInState) : 'unknown';
}

/** Well-formed harness entries only: a valid kind token, first entry per kind, at most READINESS_LIMITS.harnesses. */
export function sanitizeHarnesses(v: unknown): HarnessReadiness[] {
  if (!Array.isArray(v)) return [];
  const out: HarnessReadiness[] = [];
  const seen = new Set<string>();
  for (const x of v.slice(0, READINESS_LIMITS.scan)) {
    if (out.length >= READINESS_LIMITS.harnesses) break;
    if (!isRecord(x) || !isAgentKindToken(x.kind) || seen.has(x.kind)) continue;
    seen.add(x.kind);
    const installed = x.installed === true;
    out.push({
      kind: x.kind,
      installed,
      version: installed ? sanitizeVersion(x.version) : null,
      signIn: installed ? sanitizeSignIn(x.signIn) : 'unknown',
    });
  }
  return out;
}

/** A display name: one line, trimmed, clipped, and never an email or anything secret-shaped. */
export function sanitizeIdentityName(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined;
  const s = v.trim();
  if (!s || s.length > READINESS_LIMITS.identityName || CONTROL_RE.test(s) || s.includes('@') || looksLikeSecret(s)) return undefined;
  return s;
}

export function sanitizeGit(v: unknown): GitReadiness {
  const g = isRecord(v) ? v : {};
  const installed = g.installed === true;
  const identityConfigured = installed && g.identityConfigured === true;
  const out: GitReadiness = { installed, version: installed ? sanitizeVersion(g.version) : null, identityConfigured };
  const name = identityConfigured ? sanitizeIdentityName(g.identityName) : undefined;
  if (name) out.identityName = name;
  return out;
}

/**
 * A repository path relative to the home folder, with forward slashes; null for anything
 * else: absolute or drive paths, `~`, `..` segments, control characters, over-long paths,
 * and anything that looks like a secret or an email. A path is dropped, never clipped,
 * because a clipped path names a different folder.
 */
export function sanitizeRepoPath(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const s = v.replace(/\\/g, '/').replace(/\/{2,}/g, '/').replace(/^\.\//, '').replace(/\/$/, '');
  if (!s || s.length > READINESS_LIMITS.repoPath || CONTROL_RE.test(s)) return null;
  if (s.startsWith('/') || s.startsWith('~') || /^[A-Za-z]:/.test(s)) return null;
  if (s.split('/').some((seg) => seg === '..' || seg === '.' || seg === '')) return null;
  if (looksLikeSecret(s)) return null;
  return s;
}

export function sanitizeRepos(v: unknown): ReposFound {
  const r = isRecord(v) ? v : {};
  const paths: string[] = [];
  if (Array.isArray(r.paths)) {
    for (const p of r.paths.slice(0, READINESS_LIMITS.scan)) {
      if (paths.length >= READINESS_LIMITS.repoPaths) break;
      const clean = sanitizeRepoPath(p);
      if (clean && !paths.includes(clean)) paths.push(clean);
    }
  }
  const reported = typeof r.count === 'number' && Number.isFinite(r.count) && r.count > 0 ? Math.floor(r.count) : 0;
  const count = Math.min(Math.max(reported, paths.length), READINESS_LIMITS.repoCount);
  return { count, paths, truncated: count > paths.length };
}

/**
 * What a hub keeps of a node's readiness report: known fields only (anything else is
 * dropped), lists capped, strings clipped or dropped, and nothing that looks like a secret
 * or an email. undefined when the node sent no report (an agent that predates R-ON-4).
 */
export function sanitizeReadiness(v: unknown): MachineReadiness | undefined {
  if (!isRecord(v)) return undefined;
  return {
    checkedAt: typeof v.checkedAt === 'number' && Number.isFinite(v.checkedAt) && v.checkedAt > 0 ? v.checkedAt : 0,
    harnesses: sanitizeHarnesses(v.harnesses),
    git: sanitizeGit(v.git),
    repos: sanitizeRepos(v.repos),
  };
}

/** The UTF-8 size of a readiness report as JSON. */
export function readinessBytes(v: unknown): number {
  let n = 0;
  for (const ch of JSON.stringify(v) ?? '') {
    const c = ch.codePointAt(0) ?? 0;
    n += c < 0x80 ? 1 : c < 0x800 ? 2 : c < 0x10000 ? 3 : 4;
  }
  return n;
}

/**
 * What a server keeps of a readiness report it received: sanitizeReadiness(), and undefined
 * when there was no report or the clean report is over READINESS_MAX_BYTES (the caller then
 * keeps the last report it had).
 */
export function acceptReadiness(v: unknown): MachineReadiness | undefined {
  const clean = sanitizeReadiness(v);
  if (!clean || readinessBytes(clean) > READINESS_MAX_BYTES) return undefined;
  return clean;
}
